import { TMaybeId, TPortfolioStatus } from 'modules/api/portfolio/types'

/**
 * Lease statuses that count as a live tenancy. Everything else — draft, expired, terminated,
 * deleted — leaves the contact inactive.
 */
export const LIVE_PORTFOLIO_STATUSES: TPortfolioStatus[] = ['active', 'expiring', 'renewal']

export type TStatusAccount = {
  tag?: string | null
  portfolioId?: string | null
  propertyAddress?: string | null
}

export type TGetPortfolioStatus = (portfolioId: TMaybeId) => TPortfolioStatus

/**
 * The account that makes a contact active: the first one sitting on a live lease. It is also what
 * earns the property line under the contact's name, so the Active status and that line can never
 * disagree about which lease they are talking about.
 */
export const getLiveAccount = <T extends TStatusAccount>(
  accounts: T[],
  getPortfolioStatus: TGetPortfolioStatus,
): T | undefined => {
  // Application accounts go last so a real lease wins when a contact has both.
  const orderedAccounts = [
    ...accounts.filter(account => account.tag !== 'Application'),
    ...accounts.filter(account => account.tag === 'Application'),
  ]

  return orderedAccounts.find(
    account =>
      Boolean(account.propertyAddress) && LIVE_PORTFOLIO_STATUSES.includes(getPortfolioStatus(account.portfolioId)),
  )
}

/**
 * Whether a contact is on a live lease. Named for the fact rather than for the Active/Inactive
 * labels the pills and the Status column put on it.
 */
export const hasLiveLease = (accounts: TStatusAccount[], getPortfolioStatus: TGetPortfolioStatus): boolean =>
  Boolean(getLiveAccount(accounts, getPortfolioStatus))
